import React from 'react';
import classnames from 'classnames';
import { SignalVariant } from '../types';
import { Text } from '../text';
import { Icon, IconProps } from '../icon';
import { IconButton } from '../icon-button';
import styles from './notification.module.scss';

export type NotificationProps = {
  text: string | React.ReactNode;
  variant?: SignalVariant;
  closeButton?: {
    ariaLabel: string;
    onClick: React.MouseEventHandler<HTMLButtonElement>;
    type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
  };
  label?: string;
  className?: string;
  ariaHidden?: boolean;
  noIcon?: boolean;
};

const getIcon = (variant: SignalVariant): IconProps['name'] => {
  switch (variant) {
    case 'error':
      return 'error--medium';
    case 'informational':
      return 'info-legal--medium';
    case 'warning':
      return 'warning--medium';
    case 'success':
      return 'successful-checkmark--medium';
  }
};

export const Notification = React.forwardRef<HTMLDivElement, NotificationProps>(
  ({ label, text, variant = 'informational', className, ariaHidden, closeButton, noIcon }, ref) => {
    const classes = classnames(styles.notification, styles[`${variant}`], className);

    return (
      <div className={classes} data-testid="notification-component" aria-hidden={ariaHidden} ref={ref}>
        <div className={styles.contentContainer}>
          {!noIcon && <Icon name={getIcon(variant)} className={styles.icon} />}
          <div className={styles.content}>
            {label && <div className={styles.label}>{label}</div>}
            {typeof text === 'string' ? (
              <Text variant="paragraph-100" className={styles.text}>
                {text}
              </Text>
            ) : (
              <>{text}</>
            )}
          </div>
        </div>
        {closeButton && (
          <IconButton
            variant="ghost"
            ariaLabel={closeButton.ariaLabel}
            onClick={closeButton.onClick}
            type={closeButton.type}
            iconName="close-without-circle--small"
            className={styles.button}
            size="200"
          />
        )}
      </div>
    );
  }
);

Notification.displayName = 'Notification';
